/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { from, map, Observable } from 'rxjs';
import { SchemaName, TableInsertModel, TableName, TableRowModel, TableUpdateModel, ViewName } from './database.types';
import { ExamdojoSupabaseService } from './supabase.service';

@Injectable()
export abstract class SupabaseBaseHttpService<T extends TableName<S> | ViewName<S>, S extends SchemaName = 'public'> {
  constructor(protected readonly supabase: ExamdojoSupabaseService) {}

  abstract schema: S;
  abstract tableName: T;
  readonly storage_bucket: string = '';
  readonly id_field = 'id' as Exclude<keyof TableRowModel<T, S>, number | symbol>;

  protected query() {
    return this.supabase.client.schema(this.schema).from(this.tableName);
  }

  fetch(itemId: number | string) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .select()
        // TODO: after updating supabase-js
        .eq(this.id_field, itemId as any)
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  fetchMaybeSingle(itemId: number | string) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .select()
        .eq(this.id_field, itemId as any)
        .maybeSingle()
        .throwOnError()
        .then((response) => response.data),
    );
  }

  fetchAll() {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .select()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  createSelectQuery(selectQueryConfig: SupabaseSelectQueryConfig = {}) {
    const query = this.supabase.createSelectQuery(this.schema, this.tableName, selectQueryConfig);

    return from(query).pipe(
      map(({ data, count }) => ({
        data: data!,
        totalCount: count!,
      })),
    );
  }

  create(item: TableInsertModel<T, S>) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .insert(item as any)
        .select()
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  update(itemId: number | string, item: TableUpdateModel<T, S>) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .update(item as any)
        // TODO: after updating supabase-js
        .eq(this.id_field, itemId as any)
        .select()
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  upsert(item: TableInsertModel<T, S>) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .upsert(item as any)
        .select()
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  delete(itemId: number | string): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .delete()
        // TODO: after updating supabase-js
        .eq(this.id_field, itemId as any)
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  getPublicUrl(imageId: string) {
    return this.supabase.getPublicUrl(this.storage_bucket, imageId);
  }

  getSignedUrl(imageId: string) {
    return this.supabase.getSignedUrlForFile(this.storage_bucket, imageId);
  }

  getSignedUrls(imageIds: string[]) {
    return this.supabase.getSignedUrlsForFiles(this.storage_bucket, imageIds);
  }

  uploadImage(image: File, name?: string) {
    return this.supabase.uploadFile(this.storage_bucket, image, image.type, name);
  }

  removeImage(imageId: string) {
    return this.supabase.removeFiles(this.storage_bucket, imageId);
  }

  async getSignedUrlForImages(imageNames: Array<string | null>) {
    const data = await this.getSignedUrls(imageNames.filter(isNotNullish));

    return data.reduce<Record<string, string | null>>((acc, item) => {
      return {
        ...acc,
        ...(item.path ? { [item.path]: item.error ? null : item.signedUrl } : {}),
      };
    }, {});
  }
}
